// FloorPlanEditor.tsx
import React, { useRef, useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import {
    Box,
    IconButton,
    TextField,
    Slider,
    Paper,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Button,
    Tooltip,
    Divider,
    Typography,
    Alert
} from '@mui/material';
import {
    CleaningServices,
    Cameraswitch,
    Create,
    TextFields,
    Undo,
    RotateLeft,
    Delete,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import Cropper from 'react-cropper';
import 'cropperjs/dist/cropper.css';

// インポートするマーカー画像
import markerCross from "../assets/images/marker_cross.png";
import markerSingle from "../assets/images/marker_single.png";
import markerCrossSmall from "../assets/images/marker_cross_small.png";
import markerCircle from "../assets/images/marker_circle.png";
import markerR from "../assets/images/marker_r.png";
import markerB from "../assets/images/marker_b.png";
import markerTriangle from "../assets/images/marker_triangle.png";
import { imageSrcForLoad } from "../utils/imageUrl";

// FloorPlanEditorで使用するデータの型定義
export interface Position {
    dx: number;
    dy: number;
}

export interface Point {
    dx: number;
    dy: number;
}

export enum DrawingType {
    CLEAR = 'clear',
    PICTURE = 'picture',
    DRAW = 'draw',
    TEXT = 'text',
    UNDO = 'undo',
    ROTATE = 'rotate',
    DELETE = 'delete',
    MARKER = 'marker',
}

export interface DrawingItem {
    id: string;
    type: string;
    position: Position;
    imagePath?: string;
    size: number;
    aspectRatio?: number;
    rotation: number;
    text?: string;
    points?: Point[];
}

export interface FloorPlanEditorProps {
    initialData?: {
        id?: number;
        title: string;
        drawings: DrawingItem[] | string;
        image_data?: string | null;
        background_image_url?: string | null;
        background_image_base64?: string | null;
    };
    onDataChange?: (data: {
        id?: number; // IDプロパティを追加
        title: string;
        drawings: DrawingItem[];
        backgroundImage: HTMLImageElement | null;
        backgroundImageBase64: string | null;
        backgroundImageUrl: string | null;
        action: 'update' | 'clearBackground' | 'newBackground';
        newBackgroundFile?: File | null;
    }) => void;
}

// FloorPlanEditorRef型を定義（外部に公開するメソッド）
export interface FloorPlanEditorRef {
    getCanvas: () => HTMLCanvasElement | null;
    captureCanvas: () => Promise<Blob | null>;
    redrawCanvas: () => void;
    confirmCurrentLine: () => Promise<boolean>;
    getCurrentJsonData: () => string;
    // 背景画像の URL があるのに読み込めなかったか（署名付き URL の期限切れなど）
    hasBackgroundLoadError: () => boolean;
}

const ToolbarButton = styled(IconButton, {
    shouldForwardProp: (prop) => prop !== 'active',
})<{ active?: boolean }>(({ theme, active }) => ({
    margin: theme.spacing(0.5),
    width: 40,
    height: 40,
    backgroundColor: active ? theme.palette.primary.light : 'transparent',
    color: active ? theme.palette.primary.contrastText : theme.palette.action.active,
    '&:hover': {
        backgroundColor: active ? theme.palette.primary.main : theme.palette.action.hover,
    },
    '&.Mui-disabled': {
        color: theme.palette.action.disabled,
    },
}));

const MarkerItem = styled(Box)(({ theme }) => ({
    width: 40,
    height: 40,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    margin: theme.spacing(0.5),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    cursor: 'pointer',
    '&:hover': {
        backgroundColor: theme.palette.action.hover,
    },
}));

// マーカー情報の型定義
interface MarkerInfo {
    src: string;
    aspectRatio: number;
    size?: number; // サイズ調整用の係数（オプション）
}

// 基本マーカー画像の定義（実際に表示するための限定されたリスト）
const BASE_MARKERS: Record<string, MarkerInfo> = {
    'marker_cross.png': {
        src: markerCross,
        aspectRatio: 1
    },
    'marker_single.png': {
        src: markerSingle,
        aspectRatio: 1
    },
    'marker_cross_small.png': {
        src: markerCrossSmall,
        aspectRatio: 25 / 18,
        size: 1
    },
    'marker_circle.png': {
        src: markerCircle,
        aspectRatio: 1
    },
    'marker_r.png': {
        src: markerR,
        aspectRatio: 1
    },
    'marker_b.png': {
        src: markerB,
        aspectRatio: 1
    },
    'marker_triangle.png': {
        src: markerTriangle,
        aspectRatio: 1
    }
};

// マーカー画像の定義（パス解決用の拡張マップ）
const MARKERS: Record<string, MarkerInfo> = {
    ...BASE_MARKERS,
    // データからの読み込み用に追加のパスマッピング
    'assets/images/marker_cross.png': {
        src: markerCross,
        aspectRatio: 1
    },
    'assets/images/marker_single.png': {
        src: markerSingle,
        aspectRatio: 1
    },
    'assets/images/marker_cross_small.png': {
        src: markerCrossSmall,
        aspectRatio: 25 / 18,
        size: 1
    },
    'assets/images/marker_circle.png': {
        src: markerCircle,
        aspectRatio: 1
    },
    'assets/images/marker_r.png': {
        src: markerR,
        aspectRatio: 1
    },
    'assets/images/marker_b.png': {
        src: markerB,
        aspectRatio: 1
    },
    'assets/images/marker_triangle.png': {
        src: markerTriangle,
        aspectRatio: 1
    },
    '../assets/images/marker_cross.png': {
        src: markerCross,
        aspectRatio: 1
    },
    '../assets/images/marker_single.png': {
        src: markerSingle,
        aspectRatio: 1
    },
    '../assets/images/marker_cross_small.png': {
        src: markerCrossSmall,
        aspectRatio: 25 / 18,
        size: 1
    },
    '../assets/images/marker_circle.png': {
        src: markerCircle,
        aspectRatio: 1
    },
    '../assets/images/marker_r.png': {
        src: markerR,
        aspectRatio: 1
    },
    '../assets/images/marker_b.png': {
        src: markerB,
        aspectRatio: 1
    },
    '../assets/images/marker_triangle.png': {
        src: markerTriangle,
        aspectRatio: 1
    }
};

// キャンバス境界制限用のヘルパー関数
const constrainToCanvas = (position: Position, canvasWidth: number, canvasHeight: number): Position => {
    return {
        dx: Math.max(0, Math.min(canvasWidth, position.dx)),
        dy: Math.max(0, Math.min(canvasHeight, position.dy))
    };
};

// マーカーの境界制限を計算する関数
const constrainMarkerToCanvas = (
    position: Position,
    size: number,
    aspectRatio: number,
    canvasWidth: number,
    canvasHeight: number
): Position => {
    const width = size;
    const height = size / aspectRatio;

    const halfWidth = width / 2;
    const halfHeight = height / 2;

    return {
        dx: Math.max(halfWidth, Math.min(canvasWidth - halfWidth, position.dx)),
        dy: Math.max(halfHeight, Math.min(canvasHeight - halfHeight, position.dy))
    };
};

// テキストの境界制限を計算する関数（Canvas APIを使用して正確に計算）
const constrainTextToCanvas = (
    position: Position,
    text: string,
    fontSize: number,
    canvasWidth: number,
    canvasHeight: number
): Position => {
    // Canvas APIを使用してテキストの実際のサイズを測定
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) {
        // フォールバック：近似計算
        const approximateCharWidth = fontSize * 0.8; // 日本語文字の幅を少し多めに見積もり
        const textWidth = text.length * approximateCharWidth;
        const textHeight = fontSize;

        const halfWidth = textWidth / 2;
        const halfHeight = textHeight / 2;

        return {
            dx: Math.max(halfWidth, Math.min(canvasWidth - halfWidth, position.dx)),
            dy: Math.max(halfHeight, Math.min(canvasHeight - halfHeight, position.dy))
        };
    }

    // 実際に使用されるフォント設定でテキストサイズを測定
    ctx.font = `${fontSize}px Roboto, sans-serif`;
    const textMetrics = ctx.measureText(text);
    const textWidth = textMetrics.width;
    const textHeight = fontSize; // フォントサイズを高さとして使用

    const halfWidth = textWidth / 2;
    const halfHeight = textHeight / 2;

    return {
        dx: Math.max(halfWidth, Math.min(canvasWidth - halfWidth, position.dx)),
        dy: Math.max(halfHeight, Math.min(canvasHeight - halfHeight, position.dy))
    };
};

// forwardRefを使用してFloorPlanEditorをrefに対応させる
const FloorPlanEditor = forwardRef<FloorPlanEditorRef, FloorPlanEditorProps>((props: FloorPlanEditorProps, ref) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const cropperRef = useRef<HTMLImageElement>(null);
    const [scale, setScale] = useState<number>(3);
    const [selectedTool, setSelectedTool] = useState<DrawingType | null>(null);
    const [drawings, setDrawings] = useState<DrawingItem[]>([]);
    const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
    const [backgroundImage, setBackgroundImage] = useState<HTMLImageElement | null>(null);
    const [backgroundImageBase64, setBackgroundImageBase64] = useState<string | null>(null);
    const [backgroundImageUrl, setBackgroundImageUrl] = useState<string | null>(null);
    const [currentPoints, setCurrentPoints] = useState<Point[] | null>(null);
    const [draggingState, setDraggingState] = useState<{
        itemId: string | null;
        startOffset: Position | null;
        originalPosition: Position | null;
    }>({
        itemId: null,
        startOffset: null,
        originalPosition: null
    });

    let confirmationInProgress = false; // グローバル変数としてトラッキング

    // 追加: 重複呼び出し防止のためのフラグ
    const [isConfirmingLine, setIsConfirmingLine] = useState<boolean>(false);

    // ダイアログの状態
    const [confirmDialogOpen, setConfirmDialogOpen] = useState<boolean>(false);
    const [textDialogOpen, setTextDialogOpen] = useState<boolean>(false);
    const [textInput, setTextInput] = useState<string>('');

    // 画像トリミング用の状態
    const [cropDialogOpen, setCropDialogOpen] = useState<boolean>(false);
    const [selectedImageFile, setSelectedImageFile] = useState<File | null>(null);
    const [imageSrc, setImageSrc] = useState<string | null>(null);

    // マーカー画像のキャッシュ
    const [markerImages, setMarkerImages] = useState<Record<string, HTMLImageElement>>({});

    // 初期化完了フラグを追加
    const [isInitialized, setIsInitialized] = useState<boolean>(false);
    const [backgroundImageLoaded, setBackgroundImageLoaded] = useState<boolean>(false);
    const [markerImagesLoaded, setMarkerImagesLoaded] = useState<boolean>(false);

    // 背景画像の URL があるのに読み込めなかったか（署名付き URL の期限切れ・通信の失敗など）。
    // このまま保存で撮り直すと背景の無い画像で置き換わるため、親の保存処理はこの見取り図を
    // 編集前のまま残す（utils/floorPlanSave.ts）。表示用の state と、保存処理から読む ref を持つ。
    // 一度立てたら、読み込めたときか背景を選び直したときまで外さない
    const [backgroundLoadError, setBackgroundLoadError] = useState<boolean>(false);
    const backgroundLoadErrorRef = useRef<boolean>(false);
    const markBackgroundLoadError = (value: boolean) => {
        backgroundLoadErrorRef.current = value;
        setBackgroundLoadError(value);
    };

    const gridSize = 25;
    // キャンバスの比率を計算（600:250 = 2.4:1）
    const CANVAS_ASPECT_RATIO = 600 / 250;
    // キャンバスのサイズを定数として定義
    const CANVAS_WIDTH = 600;
    const CANVAS_HEIGHT = 250;

    // 外部に公開するメソッド
    useImperativeHandle(ref, () => ({
        // キャンバス要素を取得
        getCanvas: () => canvasRef.current,

        // キャンバスをBlobとして取得する関数（更新版）
        captureCanvas: async () => {
            const canvas = canvasRef.current;
            if (!canvas) return null;

            try {
                // 現在描画中の線を確定（もし描画中の線があれば）
                await confirmCurrentLine();

                // 少し待って描画が確実に反映されるのを待つ
                await new Promise(resolve => setTimeout(resolve, 50));

                // キャプチャモードで再描画（選択状態などを表示しない）
                redrawCanvas(true);

                // 少し待って描画が確実に反映されるのを待つ
                await new Promise(resolve => setTimeout(resolve, 50));

                // キャンバスのキャプチャを取得
                return new Promise<Blob | null>((resolve) => {
                    canvas.toBlob((blob) => {
                        resolve(blob);

                        // キャプチャ後、通常モードで再描画（選択状態などを表示）
                        setTimeout(() => {
                            redrawCanvas(false);
                        }, 50);
                    }, 'image/jpeg', 0.9);
                });
            } catch (error) {
                console.error('キャプチャ中にエラーが発生しました:', error);
                // エラー時も通常モードに戻す
                redrawCanvas(false);
                return null;
            }
        },

        // キャンバスを再描画する関数
        redrawCanvas: () => {
            redrawCanvas(false); // 通常モードで再描画
        },

        // 追加: 描画中の線を確定するメソッド
        confirmCurrentLine: async () => {
            return await confirmCurrentLine();
        },

        // 追加: 現在のJSONデータを取得するメソッド
        getCurrentJsonData: () => {
            // 常に最新のデータを使用してJSONを生成
            let allDrawings = [...drawings];
            if (selectedTool === DrawingType.DRAW && currentPoints && currentPoints.length > 1) {
                const currentLine: DrawingItem = {
                    id: `temp-${Date.now()}`,
                    type: DrawingType.DRAW.toString(),
                    position: currentPoints[0],
                    points: [...currentPoints],
                    size: 3.0,
                    rotation: 0
                };
                allDrawings.push(currentLine);
            }

            // JSON形式に変換
            interface EnhancedJsonData {
                title: string;
                drawings: DrawingItem[];
                backgroundImagePath: string | null;
                timestamp: number;
                backgroundImageBase64?: string | null;
            }

            const jsonData: EnhancedJsonData = {
                title: props.initialData?.title || '見取り図',
                drawings: allDrawings,
                backgroundImagePath: backgroundImageUrl,
                timestamp: new Date().getTime() // 常に異なる値にするためのタイムスタンプ
            };

            // backgroundImageBase64があれば追加
            if (backgroundImageBase64) {
                jsonData.backgroundImageBase64 = backgroundImageBase64;
            }

            // JSONデータをシリアライズ
            const jsonString = JSON.stringify(jsonData);
            return jsonString;
        },

        // 背景画像の URL があるのに読み込めなかったか
        hasBackgroundLoadError: () => backgroundLoadErrorRef.current
    }));

    // マーカー画像の事前読み込み（修正版）
    useEffect(() => {
        const preloadMarkerImages = async () => {
            const imgCache: Record<string, HTMLImageElement> = {};
            const loadPromises: Promise<void>[] = [];

            for (const [key, marker] of Object.entries(MARKERS)) {
                const loadPromise = new Promise<void>((resolve) => {
                    const img = new Image();
                    img.onload = () => {
                        imgCache[key] = img;
                        resolve();
                    };
                    img.onerror = () => {
                        console.warn(`Failed to load marker image: ${key}`);
                        resolve(); // エラーでも続行
                    };
                    img.src = marker.src;
                });
                loadPromises.push(loadPromise);
            }

            // すべてのマーカー画像の読み込み完了を待つ
            await Promise.all(loadPromises);

            setMarkerImages(imgCache);
            setMarkerImagesLoaded(true);
        };

        preloadMarkerImages();
    }, []);

    // 初期データの読み込み処理（修正版）
    useEffect(() => {
        if (props.initialData) {
            // drawings処理部分
            let parsedDrawings: DrawingItem[] = [];
            try {
                if (typeof props.initialData.drawings === 'string') {
                    try {
                        // JSON文字列の場合はパース
                        const parsed = JSON.parse(props.initialData.drawings);
                        // 'drawings'プロパティを持っている場合（オブジェクト形式）
                        if (parsed.drawings && Array.isArray(parsed.drawings)) {
                            parsedDrawings = parsed.drawings;
                        }
                        // 配列の場合は直接使用
                        else if (Array.isArray(parsed)) {
                            parsedDrawings = parsed;
                        }
                        // その他のオブジェクト形式の場合は空配列
                        else {
                            console.warn('Unexpected format for drawings:', parsed);
                            parsedDrawings = [];
                        }
                    } catch (e) {
                        console.error('Error parsing drawings JSON:', e);
                        parsedDrawings = [];
                    }
                } else if (Array.isArray(props.initialData.drawings)) {
                    // すでに配列の場合は直接使用
                    parsedDrawings = props.initialData.drawings;
                } else {
                    console.warn('Drawings is neither string nor array:', props.initialData.drawings);
                    parsedDrawings = [];
                }

                // 型変換：DrawingTypeの文字列表現を処理
                parsedDrawings = parsedDrawings.map(item => {
                    const newItem = { ...item };
                    // 文字列から型への変換
                    if (newItem.type === 'DrawingType.marker' || newItem.type === 'marker') {
                        newItem.type = DrawingType.MARKER.toString();
                    } else if (newItem.type === 'DrawingType.text' || newItem.type === 'text') {
                        newItem.type = DrawingType.TEXT.toString();
                    } else if (newItem.type === 'DrawingType.draw' || newItem.type === 'draw') {
                        newItem.type = DrawingType.DRAW.toString();
                    }

                    return newItem;
                });

                // 既存のアイテムをキャンバス境界内に制限
                parsedDrawings = parsedDrawings.map(item => {
                    if (item.type === DrawingType.MARKER.toString() || item.type === 'DrawingType.marker') {
                        if (item.imagePath && MARKERS[item.imagePath]) {
                            const markerInfo = MARKERS[item.imagePath];
                            const aspectRatio = item.aspectRatio || markerInfo.aspectRatio || 1.0;
                            const sizeMultiplier = markerInfo.size || 1.0;
                            const actualSize = item.size * sizeMultiplier;

                            const constrainedPosition = constrainMarkerToCanvas(
                                item.position,
                                actualSize,
                                aspectRatio,
                                CANVAS_WIDTH,
                                CANVAS_HEIGHT
                            );

                            return { ...item, position: constrainedPosition };
                        }
                    } else if (item.type === DrawingType.TEXT.toString() || item.type === 'DrawingType.text') {
                        if (item.text) {
                            const constrainedPosition = constrainTextToCanvas(
                                item.position,
                                item.text,
                                item.size,
                                CANVAS_WIDTH,
                                CANVAS_HEIGHT
                            );

                            return { ...item, position: constrainedPosition };
                        }
                    }

                    return item;
                });

                setDrawings(parsedDrawings);
            } catch (e) {
                console.error('Error processing drawings:', e);
                setDrawings([]);
            }

            // 背景画像の読み込み処理を非同期で実行
            loadBackgroundImage();
        }

        // 背景画像の読み込み処理を分離
        async function loadBackgroundImage() {
            if (!props.initialData) {
                setBackgroundImageLoaded(true);
                return;
            }

            // 背景画像の読み込み優先順位:
            // 1. background_image_url (S3 URL)
            // 2. background_image_base64 (一時的な画像データ)
            // 3. image_data (レガシーデータ)
            if (props.initialData.background_image_url) {
                try {
                    await loadImageFromUrl(props.initialData.background_image_url);
                    markBackgroundLoadError(false);
                } catch (error) {
                    console.error('背景画像の読み込みに失敗:', props.initialData.background_image_url, error);
                    // 印は、読み込めたときと背景を選び直したときだけ外す。この処理は描画のたびに
                    // やり直され、失敗のあとに線を描くと背景の URL が空で呼ばれるため、ここで外すと
                    // 失敗したことが消えてしまう
                    markBackgroundLoadError(true);
                    await loadImageFromBase64();
                }
            }
            // 一時的なBase64データがある場合
            else if (props.initialData.background_image_base64) {
                await loadImageFromBase64(props.initialData.background_image_base64);
            }
            // レガシーのimage_dataがある場合
            else if (props.initialData.image_data) {
                await loadImageFromBase64(props.initialData.image_data);
            }
            // 画像が一切ない場合
            else {
                setBackgroundImage(null);
                setBackgroundImageUrl(null);
                setBackgroundImageBase64(null);
                setBackgroundImageLoaded(true);
            }
        }

        // URLから背景画像を読み込む関数
        function loadImageFromUrl(url: string): Promise<void> {
            return new Promise((resolve, reject) => {
                const img = new Image();
                img.crossOrigin = "Anonymous"; // CORSエラーを防止

                img.onload = () => {
                    setBackgroundImage(img);
                    setBackgroundImageUrl(url);
                    setBackgroundImageBase64(null); // URLがある場合はBase64をクリア
                    setBackgroundImageLoaded(true);
                    resolve();
                };

                img.onerror = (e) => {
                    reject(e);
                };

                // 署名の無い URL にだけキャッシュ回避のタイムスタンプを足す（署名付き URL は足すと 403）
                img.src = imageSrcForLoad(url);
            });
        }

        // Base64データから背景画像を読み込む関数
        function loadImageFromBase64(base64Data?: string | null): Promise<void> {
            return new Promise((resolve) => {
                const dataToUse = base64Data || props.initialData?.background_image_base64 || props.initialData?.image_data;

                if (dataToUse) {
                    const img = new Image();
                    img.onload = () => {
                        setBackgroundImage(img);
                        setBackgroundImageBase64(dataToUse);
                        setBackgroundImageUrl(null); // Base64を使用する場合はURLをクリア
                        setBackgroundImageLoaded(true);
                        resolve();
                    };
                    img.onerror = () => {
                        console.error('Base64画像の読み込みに失敗');
                        setBackgroundImage(null);
                        setBackgroundImageUrl(null);
                        setBackgroundImageBase64(null);
                        setBackgroundImageLoaded(true);
                        resolve();
                    };
                    img.src = `data:image/jpeg;base64,${dataToUse}`;
                } else {
                    // 背景画像なし
                    setBackgroundImage(null);
                    setBackgroundImageUrl(null);
                    setBackgroundImageBase64(null);
                    setBackgroundImageLoaded(true);
                    resolve();
                }
            });
        }
    }, [props.initialData]);

    // 初期化完了チェック
    useEffect(() => {
        if (backgroundImageLoaded && markerImagesLoaded) {
            setIsInitialized(true);
        }
    }, [backgroundImageLoaded, markerImagesLoaded]);

    // キャンバスのリサイズ処理
    useEffect(() => {
        const handleResize = () => {
            if (isInitialized) {
                redrawCanvas();
            }
        };

        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, [isInitialized]);

    // 状態変更時にキャンバスを再描画（初期化完了後のみ）
    useEffect(() => {
        if (isInitialized) {
            redrawCanvas();
        }
    }, [drawings, selectedItemId, backgroundImage, currentPoints, scale, isInitialized]);

    // キャンバスのクリア
    const clearCanvas = () => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        ctx.clearRect(0, 0, canvas.width, canvas.height);
    };

    // グリッドの描画
    const drawGrid = (ctx: CanvasRenderingContext2D, width: number, height: number) => {
        ctx.strokeStyle = 'rgba(0, 0, 0, 0.2)';
        ctx.lineWidth = 1;

        for (let i = 0; i <= width; i += gridSize) {
            ctx.beginPath();
            ctx.moveTo(i, 0);
            ctx.lineTo(i, height);
            ctx.stroke();
        }

        for (let i = 0; i <= height; i += gridSize) {
            ctx.beginPath();
            ctx.moveTo(0, i);
            ctx.lineTo(width, i);
            ctx.stroke();
        }
    };

    // 点と線分の距離を計算する関数
    const distanceToLineSegment = (p: { x: number, y: number }, v: { x: number, y: number }, w: { x: number, y: number }) => {
        // 線分の長さの2乗
        const l2 = Math.pow(w.x - v.x, 2) + Math.pow(w.y - v.y, 2);

        // 線分の長さが0の場合（点）
        if (l2 === 0) {
            return Math.sqrt(Math.pow(p.x - v.x, 2) + Math.pow(p.y - v.y, 2));
        }

        // 線分上の最も近い点を見つけるためのパラメータ t
        let t = ((p.x - v.x) * (w.x - v.x) + (p.y - v.y) * (w.y - v.y)) / l2;

        // tを0〜1に制限（線分上の点になるように）
        t = Math.max(0, Math.min(1, t));

        // 線分上の最も近い点
        const projection = {
            x: v.x + t * (w.x - v.x),
            y: v.y + t * (w.y - v.y)
        };

        // 点と線分上の最も近い点との距離
        return Math.sqrt(Math.pow(p.x - projection.x, 2) + Math.pow(p.y - projection.y, 2));
    };

    // 描画アイテムの描画（修正版）
    const drawItem = (ctx: CanvasRenderingContext2D, item: DrawingItem, isCapturing: boolean = false) => {
        // キャプチャ中は選択状態を無視
        const isSelected = isCapturing ? false : item.id === selectedItemId;
        const position = { x: item.position.dx, y: item.position.dy };

        ctx.save();
        // 回転の中心点を設定
        ctx.translate(position.x, position.y);
        ctx.rotate(item.rotation);
        ctx.translate(-position.x, -position.y);

        // 選択中のアイテムは赤く表示（キャプチャ中でなければ）
        ctx.strokeStyle = isSelected ? '#f44336' : '#000000';
        ctx.lineWidth = isSelected ? 4 : 3;

        if (item.type === DrawingType.MARKER || item.type === DrawingType.MARKER.toString() || item.type === 'DrawingType.marker') {
            // マーカーパスをMARKERSから検索
            if (item.imagePath) {
                // MARKERSに登録されているか確認
                const markerInfo = MARKERS[item.imagePath] || null;

                if (markerInfo && markerImages[item.imagePath]) {
                    // マーカー画像がキャッシュされている場合
                    const img = markerImages[item.imagePath];

                    // アスペクト比の取得（item内のアスペクト比を優先）
                    const aspectRatio = item.aspectRatio || markerInfo.aspectRatio || 1.0;

                    // サイズ係数を適用（デフォルトは1.0）
                    const sizeMultiplier = markerInfo.size || 1.0;
                    const actualSize = item.size * sizeMultiplier;

                    // マーカーの描画
                    // aspectRatioは「幅 ÷ 高さ」なので、高さを計算するときは幅をaspectRatioで割る
                    ctx.drawImage(
                        img,
                        position.x - actualSize / 2,                    // X座標（中央揃え）
                        position.y - (actualSize / aspectRatio) / 2,    // Y座標（中央揃え）
                        actualSize,                                     // 幅
                        actualSize / aspectRatio                        // 高さ = 幅 ÷ アスペクト比
                    );

                    // 選択枠の描画（キャプチャ中でなければ）
                    if (isSelected && !isCapturing) {
                        ctx.strokeRect(
                            position.x - actualSize / 2,
                            position.y - (actualSize / aspectRatio) / 2,
                            actualSize,
                            actualSize / aspectRatio
                        );
                    }
                } else {
                    // マーカー画像が見つからない場合の代替表示
                    console.warn(`Marker not found or not loaded for path: ${item.imagePath}`);

                    // 代替表示：円形のプレースホルダー
                    ctx.fillStyle = '#FF0000';
                    ctx.beginPath();
                    ctx.arc(position.x, position.y, item.size / 2, 0, Math.PI * 2);
                    ctx.fill();

                    // 選択枠の描画（キャプチャ中でなければ）
                    if (isSelected && !isCapturing) {
                        ctx.strokeRect(
                            position.x - item.size / 2,
                            position.y - item.size / 2,
                            item.size,
                            item.size
                        );
                    }
                }
            }
        } else if (item.type === DrawingType.TEXT || item.type === DrawingType.TEXT.toString() || item.type === 'DrawingType.text') {
            if (item.text) {
                ctx.font = `${item.size}px Roboto, sans-serif`;
                // キャプチャ中は選択状態に関わらず黒で描画
                ctx.fillStyle = isSelected && !isCapturing ? '#f44336' : '#000000';
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';
                ctx.fillText(item.text, position.x, position.y);

                // 選択状態の表示（キャプチャ中でなければ）
                if (isSelected && !isCapturing) {
                    const textMetrics = ctx.measureText(item.text);
                    const textHeight = item.size; // 近似値
                    ctx.strokeRect(
                        position.x - textMetrics.width / 2,
                        position.y - textHeight / 2,
                        textMetrics.width,
                        textHeight
                    );
                }
            }
        } else if (item.type === DrawingType.DRAW ||
            item.type === DrawingType.DRAW.toString() ||
            item.type === 'DrawingType.draw' ||
            item.type === 'draw') {
            if (item.points && item.points.length > 1) {
                // 線の描画
                ctx.beginPath();
                ctx.moveTo(item.points[0].dx, item.points[0].dy);
                for (let i = 1; i < item.points.length; i++) {
                    ctx.lineTo(item.points[i].dx, item.points[i].dy);
                }

                // 選択状態に応じて線の太さと色を変更
                ctx.strokeStyle = isSelected ? '#f44336' : '#000000';
                ctx.lineWidth = isSelected ? 5 : 3;
                ctx.stroke();

                // 頂点の描画（キャプチャ中でなければ）
                if (!isCapturing) {
                    ctx.fillStyle = isSelected ? '#f44336' : '#000000';
                    for (const point of item.points) {
                        ctx.beginPath();
                        ctx.arc(point.dx, point.dy, isSelected ? 4 : 2, 0, Math.PI * 2);
                        ctx.fill();
                    }
                }
            }
        }

        ctx.restore();
    };

    // redrawCanvas関数も修正して一貫性を保ちます
    const redrawCanvas = (isCapturing: boolean = false) => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        // 初期化が完了していない場合は描画しない
        if (!isInitialized) return;

        // キャンバスをクリア
        clearCanvas();

        // キャプチャモードの場合、白い背景で明示的に塗りつぶす
        if (isCapturing) {
            ctx.fillStyle = '#FFFFFF';
            ctx.fillRect(0, 0, canvas.width, canvas.height);
        }

        // 背景画像の描画
        if (backgroundImage) {
            try {
                ctx.drawImage(backgroundImage, 0, 0, canvas.width, canvas.height);
            } catch (error) {
                console.error('背景画像の描画中にエラーが発生しました:', error);
                // エラーメッセージを画面に表示
                ctx.font = '14px Arial';
                ctx.fillStyle = '#FF0000';
                ctx.fillText('背景画像の表示に失敗しました', 20, 30);
            }
        }

        // グリッドの描画
        drawGrid(ctx, canvas.width, canvas.height);

        // 描画アイテムの描画 - 配列かどうか確認
        if (Array.isArray(drawings)) {
            for (const item of drawings) {
                drawItem(ctx, item, isCapturing);
            }
        }

        // 現在描画中の線を描画（キャプチャ中でなければ）
        if (!isCapturing && currentPoints && currentPoints.length > 0) {
            ctx.strokeStyle = '#000000';
            ctx.lineWidth = 3;
            ctx.beginPath();
            ctx.moveTo(currentPoints[0].dx, currentPoints[0].dy);
            for (let i = 1; i < currentPoints.length; i++) {
                ctx.lineTo(currentPoints[i].dx, currentPoints[i].dy);
            }
            ctx.stroke();

            // 頂点を描画
            ctx.fillStyle = '#000000';
            for (const point of currentPoints) {
                ctx.beginPath();
                ctx.arc(point.dx, point.dy, 2, 0, Math.PI * 2);
                ctx.fill();
            }
        }
    };

    // captureCanvas関数の改良バージョン
    const captureCanvas = async () => {
        const canvas = canvasRef.current;
        if (!canvas) return null;

        try {
            // 元のキャンバスの状態を保存するためにオフスクリーンキャンバスを作成
            const offscreenCanvas = document.createElement('canvas');
            offscreenCanvas.width = canvas.width;
            offscreenCanvas.height = canvas.height;
            const ctx = offscreenCanvas.getContext('2d');

            if (!ctx) return null;

            // 背景を白色で明示的に塗りつぶす
            ctx.fillStyle = '#FFFFFF';
            ctx.fillRect(0, 0, canvas.width, canvas.height);

            // 背景画像があれば描画
            if (backgroundImage) {
                try {
                    ctx.drawImage(backgroundImage, 0, 0, canvas.width, canvas.height);
                } catch (error) {
                    console.error('背景画像の描画中にエラーが発生しました:', error);
                }
            }

            // グリッドの描画（背景は白なので、グリッドがしっかり見えるようにする）
            ctx.strokeStyle = 'rgba(0, 0, 0, 0.2)';
            ctx.lineWidth = 1;

            for (let i = 0; i <= canvas.width; i += gridSize) {
                ctx.beginPath();
                ctx.moveTo(i, 0);
                ctx.lineTo(i, canvas.height);
                ctx.stroke();
            }

            for (let i = 0; i <= canvas.height; i += gridSize) {
                ctx.beginPath();
                ctx.moveTo(0, i);
                ctx.lineTo(canvas.width, i);
                ctx.stroke();
            }

            // 各描画アイテムを描画（キャプチャモードで選択表示なし）
            if (Array.isArray(drawings)) {
                for (const item of drawings) {
                    drawItem(ctx, item, true); // isCapturing=trueで選択表示を消す
                }
            }

            // オフスクリーンキャンバスからBlobを取得
            return new Promise<Blob | null>((resolve) => {
                offscreenCanvas.toBlob((blob) => {
                    resolve(blob);

                    // キャプチャ完了後、元のキャンバスを通常モードで再描画
                    setTimeout(() => {
                        redrawCanvas(false);
                    }, 50);
                }, 'image/jpeg', 0.9);
            });
        } catch (error) {
            console.error('キャプチャ中にエラーが発生しました:', error);
            // エラー時も通常モードに戻す
            redrawCanvas(false);
            return null;
        }
    };

    // グリッド上の位置に合わせる関数
    const snapToGrid = (position: { x: number, y: number }): Position => {
        const x = Math.round(position.x / gridSize) * gridSize;
        const y = Math.round(position.y / gridSize) * gridSize;
        return { dx: x, dy: y };
    };

    // 画像選択処理
    const handlePickImage = () => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'image/*';

        input.onchange = async (e) => {
            const target = e.target as HTMLInputElement;
            if (!target.files || !target.files[0]) return;

            const file = target.files[0];
            setSelectedImageFile(file);

            // ファイルを読み込みトリミングダイアログを表示
            const reader = new FileReader();
            reader.onload = (event) => {
                if (!event.target) return;
                const result = event.target.result;
                if (typeof result !== 'string') return;

                setImageSrc(result);
                setCropDialogOpen(true);
            };
            reader.readAsDataURL(file);
        };

        input.click();
    };

    // トリミング完了時の処理
    const handleCropComplete = (croppedImageUrl: string) => {
        const imageElement: any = cropperRef?.current;
        const cropper: any = imageElement?.cropper;

        if (cropper) {
            // キャンバスからトリミングされた画像を取得
            cropper.getCroppedCanvas().toBlob(async (blob: Blob) => {
                if (blob) {
                    // トリミングされた画像をFileオブジェクトに変換
                    const fileName = selectedImageFile ? selectedImageFile.name : 'cropped-image.jpg';
                    const fileType = selectedImageFile ? selectedImageFile.type : 'image/jpeg';

                    // トリミングされたBlobからFileオブジェクトを作成
                    const croppedFile = new File([blob], fileName, {
                        type: fileType,
                        lastModified: new Date().getTime()
                    });

                    // 画像をロード
                    const img = new Image();
                    img.onload = () => {
                        setBackgroundImage(img);

                        // Base64形式に変換
                        const canvas = document.createElement('canvas');
                        canvas.width = img.width;
                        canvas.height = img.height;
                        const ctx = canvas.getContext('2d');
                        if (ctx) {
                            ctx.drawImage(img, 0, 0);
                            const base64Data = canvas.toDataURL('image/jpeg').split(',')[1];

                            // Base64データを設定（トリミング完了後に背景画像として表示するため）
                            setBackgroundImageBase64(base64Data);
                            setBackgroundImageUrl(null); // URLをクリア（一時的にBase64を使用）
                            // 背景を選び直したので、読み込めなかった元の背景のことは忘れて保存させる
                            markBackgroundLoadError(false);

                            // 変更を親コンポーネントに通知
                            if (props.onDataChange) {
                                props.onDataChange({
                                    title: props.initialData?.title || '見取り図',
                                    drawings,
                                    backgroundImage: img,
                                    backgroundImageBase64: base64Data, // Base64データを通知
                                    backgroundImageUrl: null, // URLはnull
                                    action: 'newBackground',
                                    newBackgroundFile: croppedFile
                                });
                            }
                        }
                    };
                    img.src = croppedImageUrl;

                    setCropDialogOpen(false);
                }
            }, 'image/jpeg');
        }
    };

    // ESCキーで線を中止する処理を追加
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            // ESCキーが押された場合
            if (e.key === 'Escape') {
                if (selectedTool === DrawingType.DRAW && currentPoints) {
                    setCurrentPoints(null);
                    setSelectedTool(null);
                }
            }
            // Enterキーが押された場合
            else if (e.key === 'Enter') {
                confirmCurrentLine();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [selectedTool, currentPoints, drawings]);

    // 修正: キャンバスクリック処理
    const handleCanvasClick = (e: React.MouseEvent<HTMLCanvasElement>) => {
        // 処理中のときはクリックを無視
        if (confirmationInProgress) {
            return;
        }

        if (!canvasRef.current) return;

        const rect = canvasRef.current.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        const position = { x, y };

        // 描画モードの場合
        if (selectedTool === DrawingType.DRAW) {
            const snappedPosition = snapToGrid(position);
            // キャンバス境界内に制限
            const constrainedPosition = constrainToCanvas(snappedPosition, CANVAS_WIDTH, CANVAS_HEIGHT);

            if (currentPoints === null) {
                // 新しい線の開始 - 選択状態をリセット
                setSelectedItemId(null);
                setCurrentPoints([constrainedPosition]);
            } else {
                // 新しい点が最後の点から十分に離れている場合のみ追加
                const lastPoint = currentPoints[currentPoints.length - 1];
                const distance = Math.sqrt(
                    Math.pow(constrainedPosition.dx - lastPoint.dx, 2) +
                    Math.pow(constrainedPosition.dy - lastPoint.dy, 2)
                );

                // 最小距離を設定（グリッドサイズの半分など）
                if (distance >= gridSize / 2) {
                    // 新しい配列を作成して更新
                    const newPoints = [...currentPoints, constrainedPosition];
                    setCurrentPoints(newPoints);
                }
            }
            return;
        }

        // 他のモードの場合は現在の線を確定
        confirmCurrentLine();

        // アイテム選択処理
        let nearestItem: DrawingItem | null = null;
        let minDistance = 25.0; // 判定範囲

        for (const item of drawings) {
            // 線の選択処理を改善
            if (item.type === DrawingType.DRAW.toString() ||
                item.type === 'DrawingType.draw' ||
                item.type === 'draw') {
                if (item.points && item.points.length > 1) {
                    // 各点について最短距離を計算
                    let itemSelected = false;
                    for (const point of item.points) {
                        const distance = Math.sqrt(
                            Math.pow(point.dx - position.x, 2) +
                            Math.pow(point.dy - position.y, 2)
                        );

                        if (distance < minDistance) {
                            minDistance = distance;
                            nearestItem = item;
                            itemSelected = true;
                            break;
                        }
                    }

                    // まだ選択されていない場合、線分上の最短距離を計算
                    if (!itemSelected) {
                        for (let i = 0; i < item.points.length - 1; i++) {
                            const p1 = { x: item.points[i].dx, y: item.points[i].dy };
                            const p2 = { x: item.points[i + 1].dx, y: item.points[i + 1].dy };

                            const distance = distanceToLineSegment(position, p1, p2);

                            if (distance < minDistance) {
                                minDistance = distance;
                                nearestItem = item;
                                break;
                            }
                        }
                    }
                }
            } else {
                // マーカーやテキストの選択処理（既存）
                const itemPosition = { x: item.position.dx, y: item.position.dy };
                const distance = Math.sqrt(
                    Math.pow(itemPosition.x - position.x, 2) +
                    Math.pow(itemPosition.y - position.y, 2)
                );

                if (distance < minDistance) {
                    // マーカーやテキストに対する既存の処理
                    if (item.type === DrawingType.MARKER.toString() ||
                        item.type === DrawingType.TEXT.toString() ||
                        item.type === 'DrawingType.marker' ||
                        item.type === 'DrawingType.text') {
                        minDistance = distance;
                        nearestItem = item;
                    }
                }
            }
        }

        setSelectedItemId(nearestItem?.id || null);
    };

    // ドラッグ開始処理（境界制限付き）
    const handleCanvasMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
        if (!canvasRef.current || !selectedItemId) return;

        const rect = canvasRef.current.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        const item = drawings.find(item => item.id === selectedItemId);
        if (!item) return;

        setDraggingState({
            itemId: selectedItemId,
            startOffset: { dx: x, dy: y },
            originalPosition: { ...item.position }
        });
    };

    // ドラッグ処理（境界制限付き）
    const handleCanvasMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
        if (!canvasRef.current || !draggingState.itemId || !draggingState.startOffset || !draggingState.originalPosition) return;

        const rect = canvasRef.current.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        const deltaX = x - draggingState.startOffset.dx;
        const deltaY = y - draggingState.startOffset.dy;

        let newPosition = {
            dx: draggingState.originalPosition.dx + deltaX,
            dy: draggingState.originalPosition.dy + deltaY
        };

        // ドラッグ中のアイテムの種類に応じて境界制限を適用
        const item = drawings.find(item => item.id === draggingState.itemId);
        if (item) {
            if (item.type === DrawingType.MARKER.toString() || item.type === 'DrawingType.marker') {
                if (item.imagePath && MARKERS[item.imagePath]) {
                    const markerInfo = MARKERS[item.imagePath];
                    const aspectRatio = item.aspectRatio || markerInfo.aspectRatio || 1.0;
                    const sizeMultiplier = markerInfo.size || 1.0;
                    const actualSize = item.size * sizeMultiplier;

                    newPosition = constrainMarkerToCanvas(
                        newPosition,
                        actualSize,
                        aspectRatio,
                        CANVAS_WIDTH,
                        CANVAS_HEIGHT
                    );
                }
            } else if (item.type === DrawingType.TEXT.toString() || item.type === 'DrawingType.text') {
                if (item.text) {
                    newPosition = constrainTextToCanvas(
                        newPosition,
                        item.text,
                        item.size,
                        CANVAS_WIDTH,
                        CANVAS_HEIGHT
                    );
                }
            } else {
                // その他のアイテムは基本的な境界制限
                newPosition = constrainToCanvas(newPosition, CANVAS_WIDTH, CANVAS_HEIGHT);
            }
        }

        // 更新されたdrawings配列を作成
        const updatedDrawings = drawings.map(drawingItem =>
            drawingItem.id === draggingState.itemId
                ? { ...drawingItem, position: newPosition }
                : drawingItem
        );

        setDrawings(updatedDrawings);
    };

    // ドラッグ終了処理
    const handleCanvasMouseUp = () => {
        // ドラッグが完了したら親コンポーネントに変更を通知
        if (draggingState.itemId && props.onDataChange) {
            props.onDataChange({
                title: props.initialData?.title || '見取り図',
                drawings,
                backgroundImage,
                backgroundImageBase64,
                backgroundImageUrl,
                action: 'update'
            });
        }

        // ドラッグ状態をリセット
        setDraggingState({
            itemId: null,
            startOffset: null,
            originalPosition: null
        });
    };

    // 修正: ダブルクリックで線を確定する処理
    const handleCanvasDoubleClick = (e: React.MouseEvent<HTMLCanvasElement>) => {
        // 処理中のときはダブルクリックを無視
        if (confirmationInProgress) {
            return;
        }

        // 描画モードで、かつ点が2つ以上ある場合のみ処理
        if (selectedTool === DrawingType.DRAW && currentPoints && currentPoints.length > 1) {
            // イベントバブリングを防止
            e.stopPropagation();

            // 線を確定する（非同期処理をawaitしない代わりに.thenを使用）
            confirmCurrentLine().then(success => {
                if (success) {
                    // 線が確定されたら、新しい線の開始点を設定
                    setTimeout(() => {
                        if (!confirmationInProgress && selectedTool === DrawingType.DRAW) {
                            const rect = canvasRef.current?.getBoundingClientRect();
                            if (rect) {
                                const x = e.clientX - rect.left;
                                const y = e.clientY - rect.top;
                                const snappedPosition = snapToGrid({ x, y });
                                const constrainedPosition = constrainToCanvas(snappedPosition, CANVAS_WIDTH, CANVAS_HEIGHT);
                                setCurrentPoints([constrainedPosition]);
                            }
                        }
                    }, 100);
                }
            });
        }
    };

    // 修正: マーカードラッグ＆ドロップの前に線を確定し、選択状態と描画モードをリセット
    const handleCanvasDragOver = (e: React.DragEvent<HTMLCanvasElement>) => {
        e.preventDefault();

        // 描画中の線があれば確定
        if (selectedTool === DrawingType.DRAW && currentPoints && currentPoints.length > 1) {
            confirmCurrentLine();
        }

        // 描画モードがオンの場合はオフにする
        if (selectedTool === DrawingType.DRAW) {
            setSelectedTool(null);
            setCurrentPoints(null);
        }

        // ドラッグオーバー時に選択状態をリセット
        if (selectedItemId) {
            setSelectedItemId(null);
        }
    };

    // 修正: コンポーネントがアンマウントされる前に線を確定
    useEffect(() => {
        return () => {
            // コンポーネントがアンマウントされる前に線を確定
            if (selectedTool === DrawingType.DRAW && currentPoints && currentPoints.length > 1 && !isConfirmingLine) {
                confirmCurrentLine();
            }
        };
    }, []);  // 空の依存配列で、コンポーネントのマウント/アンマウント時のみ実行

    // 修正: ツール選択処理
    const handleSelectTool = (type: DrawingType) => {
        // 処理中なら無視
        if (confirmationInProgress) {
            return;
        }

        // 描画ツールの特別な処理
        if (type === DrawingType.DRAW) {
            // 現在描画ツールが選択されている場合
            if (selectedTool === DrawingType.DRAW) {
                // 描画中の線があれば確定してからツールをオフ
                if (currentPoints && currentPoints.length > 1) {
                    confirmCurrentLine().then(() => {
                        setSelectedTool(null); // ツールをオフ
                    });
                } else {
                    // 描画中でなければ単純にツールをオフ
                    setCurrentPoints(null);
                    setSelectedTool(null);
                }
                return;
            } else {
                // 他のツールから描画ツールに切り替える場合
                // 選択状態をリセット
                setSelectedItemId(null);
                // 現在描画中の線があれば確定
                if (currentPoints && currentPoints.length > 1) {
                    confirmCurrentLine().then(() => {
                        setSelectedTool(type);
                    });
                } else {
                    setSelectedTool(type);
                }
                return;
            }
        }

        // 他のツールが選択された場合の共通処理
        // 1. 現在描画中の線があれば確定
        // 2. 描画ツールがオンの場合はオフにする
        // 3. 選択状態をリセット
        if (selectedTool === DrawingType.DRAW && currentPoints && currentPoints.length > 1) {
            confirmCurrentLine().then(() => {
                // 線を確定した後、描画ツールをオフにして他のツール処理へ
                setSelectedTool(null);
                setCurrentPoints(null);
                setSelectedItemId(null);
                proceedWithToolSelection();
            });
            return;
        } else if (selectedTool === DrawingType.DRAW) {
            // 描画中でない場合も描画ツールをオフにする
            setSelectedTool(null);
            setCurrentPoints(null);
            setSelectedItemId(null);
        } else {
            // 描画ツールでない場合は選択状態のみリセット
            setSelectedItemId(null);
        }

        // 線の確定が不要な場合は直接ツール選択処理へ
        proceedWithToolSelection();

        // ツール選択の実際の処理を行う関数
        function proceedWithToolSelection() {
            if (type === DrawingType.CLEAR) {
                setConfirmDialogOpen(true);
                return;
            }

            if (type === DrawingType.PICTURE) {
                // 背景画像がある場合は削除する
                if (backgroundImage) {
                    setBackgroundImage(null);
                    setBackgroundImageBase64(null);
                    setBackgroundImageUrl(null);

                    // 変更を親コンポーネントに通知
                    if (props.onDataChange) {
                        props.onDataChange({
                            title: props.initialData?.title || '見取り図',
                            drawings,
                            backgroundImage: null,
                            backgroundImageBase64: null,
                            backgroundImageUrl: null,
                            action: 'clearBackground'
                        });
                    }
                } else {
                    // 背景画像がない場合は画像選択ダイアログを表示
                    handlePickImage();
                }
                return;
            }

            // 消去ツールが選択された場合
            if (type === DrawingType.DELETE && selectedItemId) {
                // 重複した線を含めて、選択されたアイテムを完全に削除
                const updatedDrawings = drawings.filter(item => item.id !== selectedItemId);

                // 同じ線の可能性がある重複アイテムも削除
                const selectedItem = drawings.find(item => item.id === selectedItemId);

                if (selectedItem && selectedItem.type === DrawingType.DRAW.toString()) {
                    const similarDrawings = updatedDrawings.filter(item =>
                        item.type === DrawingType.DRAW.toString() &&
                        JSON.stringify(item.points) === JSON.stringify(selectedItem.points)
                    );

                    // 同じ線の全てのインスタンスを削除
                    similarDrawings.forEach(similarItem => {
                        const index = updatedDrawings.findIndex(item => item.id === similarItem.id);
                        if (index !== -1) {
                            updatedDrawings.splice(index, 1);
                        }
                    });
                }

                setDrawings(updatedDrawings);
                setSelectedItemId(null);
                setSelectedTool(null);

                // 変更を親コンポーネントに通知
                if (props.onDataChange) {
                    props.onDataChange({
                        title: props.initialData?.title || '見取り図',
                        drawings: updatedDrawings,
                        backgroundImage,
                        backgroundImageBase64,
                        backgroundImageUrl,
                        action: 'update'
                    });
                }
                return;
            }

            // 回転ツールが選択された場合
            if (type === DrawingType.ROTATE && selectedItemId) {
                const updatedDrawings = drawings.map(item =>
                    item.id === selectedItemId
                        ? { ...item, rotation: item.rotation + Math.PI / 2 }
                        : item
                );
                setDrawings(updatedDrawings);
                setSelectedTool(null);

                // 変更を親コンポーネントに通知
                if (props.onDataChange) {
                    props.onDataChange({
                        title: props.initialData?.title || '見取り図',
                        drawings: updatedDrawings,
                        backgroundImage,
                        backgroundImageBase64,
                        backgroundImageUrl,
                        action: 'update'
                    });
                }
                return;
            }

            // テキストツールが選択された場合
            if (type === DrawingType.TEXT) {
                // テキストツール選択時に選択状態をリセット（既に上でリセット済み）
                setTextInput('');
                setTextDialogOpen(true);
                return;
            }

            setSelectedTool(type);
        }
    };

    // 元に戻す処理
    const handleUndo = () => {
        if (currentPoints && currentPoints.length > 0) {
            // 最後の点を削除
            setCurrentPoints(currentPoints.slice(0, -1));

            // もし点が1つだけになったら、描画をキャンセル
            if (currentPoints.length === 1) {
                setCurrentPoints(null);
                setSelectedTool(null);
            }
        }
    };

    // confirmCurrentLine関数を更新
    const confirmCurrentLine = async (): Promise<boolean> => {
        // 重複呼び出し防止（すでに処理中なら何もしない）
        if (confirmationInProgress) {
            return false;
        }

        // 描画モードで、かつ点が2つ以上ある場合のみ処理
        if (selectedTool === DrawingType.DRAW && currentPoints && currentPoints.length > 1) {
            // 処理中フラグを設定
            confirmationInProgress = true;
            setIsConfirmingLine(true);

            try {
                // 新しい線オブジェクトを作成
                const newItem: DrawingItem = {
                    id: Date.now().toString(),
                    type: "DrawingType.draw",
                    position: currentPoints[0],
                    points: [...currentPoints],
                    size: 3.0, // 線の太さ
                    rotation: 0
                };

                // 既存の同じ形状の線がないか確認（重複防止）
                const isDuplicate = drawings.some(item => {
                    if (item.type !== "DrawingType.draw" && item.type !== "draw") return false;
                    if (!item.points || item.points.length !== currentPoints.length) return false;

                    // 全ての点が一致するか確認
                    return item.points.every((point, index) =>
                        point.dx === currentPoints[index].dx && point.dy === currentPoints[index].dy
                    );
                });

                // 重複していない場合のみ追加
                if (!isDuplicate) {
                    const updatedDrawings = [...drawings, newItem];

                    // ★★★ 重要: このセットは非同期的に適用されるため、即時には反映されない ★★★
                    setDrawings(updatedDrawings);

                    // onDataChangeを呼び出して親コンポーネントに変更を通知
                    if (props.onDataChange) {
                        props.onDataChange({
                            title: props.initialData?.title || '見取り図',
                            drawings: updatedDrawings,  // 最新の描画データを使用
                            backgroundImage,
                            backgroundImageBase64,
                            backgroundImageUrl,
                            action: 'update'
                        });
                    }
                }

                // 描画状態をリセット
                setCurrentPoints(null);

                // 描画データと状態が更新されるのを待つ
                await new Promise(resolve => setTimeout(resolve, 100));

                // 処理完了フラグをリセット
                confirmationInProgress = false;
                setIsConfirmingLine(false);

                return true; // 線を確定したことを示す
            } catch (e) {
                console.error('[confirmCurrentLine] エラー発生:', e);
                confirmationInProgress = false;
                setIsConfirmingLine(false);
                return false;
            }
        } else {
            return false; // 線を確定しなかったことを示す
        }
    };

    // テキスト追加処理（境界制限付き）
    const handleAddText = () => {
        if (!textInput) {
            setTextDialogOpen(false);
            return;
        }

        if (canvasRef.current) {
            const canvas = canvasRef.current;
            const centerX = canvas.width / 2;
            const centerY = canvas.height / 2;
            const fontSize = 25.0;

            // テキストを境界内に制限
            const constrainedPosition = constrainTextToCanvas(
                { dx: centerX, dy: centerY },
                textInput,
                fontSize,
                CANVAS_WIDTH,
                CANVAS_HEIGHT
            );

            const newItem: DrawingItem = {
                id: Date.now().toString(),
                type: DrawingType.TEXT.toString(),
                position: constrainedPosition,
                text: textInput,
                size: fontSize,
                rotation: 0
            };

            const newDrawings = [...drawings, newItem];
            setDrawings(newDrawings);

            // 新しいテキストを追加したときは選択状態をリセット
            setSelectedItemId(null);

            // 変更を親コンポーネントに通知
            if (props.onDataChange) {
                props.onDataChange({
                    title: props.initialData?.title || '見取り図',
                    drawings: newDrawings,
                    backgroundImage,
                    backgroundImageBase64,
                    backgroundImageUrl,
                    action: 'update'
                });
            }
        }

        setTextDialogOpen(false);
        setSelectedTool(null);
    };

    // マーカー追加処理（境界制限付き）
    const handleAddMarker = (markerPath: string, x?: number, y?: number) => {
        if (canvasRef.current && MARKERS[markerPath]) {
            const canvas = canvasRef.current;
            // x, yが指定されていればその位置、なければキャンバスの中央に配置
            const centerX = x !== undefined ? x : canvas.width / 2;
            const centerY = y !== undefined ? y : canvas.height / 2;

            const markerInfo = MARKERS[markerPath];
            const aspectRatio = markerInfo.aspectRatio;

            // 基本サイズを計算
            const baseSize = 25.0 * scale / 3;
            // サイズ係数を適用（デフォルトは1.0）
            const sizeMultiplier = markerInfo.size || 1.0;
            const actualSize = baseSize * sizeMultiplier;

            // マーカーを境界内に制限
            const constrainedPosition = constrainMarkerToCanvas(
                { dx: centerX, dy: centerY },
                actualSize,
                aspectRatio,
                CANVAS_WIDTH,
                CANVAS_HEIGHT
            );

            const newItem: DrawingItem = {
                id: Date.now().toString(),
                type: DrawingType.MARKER.toString(),
                position: constrainedPosition,
                imagePath: markerPath,
                size: baseSize,  // 基本サイズを設定（描画時に係数が適用される）
                aspectRatio,
                rotation: 0
            };

            const newDrawings = [...drawings, newItem];
            setDrawings(newDrawings);

            // 新しいマーカーを追加したときは選択状態をリセット
            setSelectedItemId(null);

            // 変更を親コンポーネントに通知
            if (props.onDataChange) {
                props.onDataChange({
                    title: props.initialData?.title || '見取り図',
                    drawings: newDrawings,
                    backgroundImage,
                    backgroundImageBase64,
                    backgroundImageUrl,
                    action: 'update'
                });
            }
        }
    };

    // クリア処理の確認
    const handleClearConfirm = () => {
        const floorPlanId = props.initialData?.id !== undefined ? props.initialData.id : 'undefined';

        // 空の配列のコピーを作成
        const emptyDrawings: React.SetStateAction<DrawingItem[]> = [];

        setDrawings(emptyDrawings);
        setCurrentPoints(null);
        setSelectedItemId(null);
        setSelectedTool(null);
        setConfirmDialogOpen(false);

        // 変更を親コンポーネントに通知
        if (props.onDataChange) {
            props.onDataChange({
                id: props.initialData?.id, // IDを含める
                title: props.initialData?.title || '見取り図',
                drawings: [...emptyDrawings], // 空の配列の新しいコピーを渡す
                backgroundImage,
                backgroundImageBase64,
                backgroundImageUrl,
                action: 'update'
            });
        }
    };

    // スケール変更処理（境界制限付き）
    const handleScaleChange = (event: Event, newValue: number | number[]) => {
        const newScale = newValue as number;
        setScale(newScale);

        // 選択中のアイテムがある場合、サイズを変更し境界内に制限
        if (selectedItemId) {
            const updatedDrawings = drawings.map(item => {
                if (item.id === selectedItemId &&
                    (item.type === DrawingType.MARKER.toString() || item.type === DrawingType.TEXT.toString() ||
                        item.type === 'DrawingType.marker' || item.type === 'DrawingType.text')) {

                    // スケール3を基準にサイズを計算
                    const baseScale = newScale / 3.0;
                    const newSize = baseScale * 25.0;

                    let constrainedPosition = item.position;

                    // マーカーの場合の境界制限
                    if (item.type === DrawingType.MARKER.toString() || item.type === 'DrawingType.marker') {
                        if (item.imagePath && MARKERS[item.imagePath]) {
                            const markerInfo = MARKERS[item.imagePath];
                            const aspectRatio = item.aspectRatio || markerInfo.aspectRatio || 1.0;
                            const sizeMultiplier = markerInfo.size || 1.0;
                            const actualSize = newSize * sizeMultiplier;

                            constrainedPosition = constrainMarkerToCanvas(
                                item.position,
                                actualSize,
                                aspectRatio,
                                CANVAS_WIDTH,
                                CANVAS_HEIGHT
                            );
                        }
                    }
                    // テキストの場合の境界制限
                    else if (item.type === DrawingType.TEXT.toString() || item.type === 'DrawingType.text') {
                        if (item.text) {
                            constrainedPosition = constrainTextToCanvas(
                                item.position,
                                item.text,
                                newSize,
                                CANVAS_WIDTH,
                                CANVAS_HEIGHT
                            );
                        }
                    }

                    return { ...item, size: newSize, position: constrainedPosition };
                }
                return item;
            });

            setDrawings(updatedDrawings);

            // 変更を親コンポーネントに通知
            if (props.onDataChange) {
                props.onDataChange({
                    title: props.initialData?.title || '見取り図',
                    drawings: updatedDrawings,
                    backgroundImage,
                    backgroundImageBase64,
                    backgroundImageUrl,
                    action: 'update'
                });
            }
        }
    };

    // キャンバス上でのドロップ処理（境界制限付き）
    const handleCanvasDrop = (e: React.DragEvent<HTMLCanvasElement>) => {
        e.preventDefault();
        if (!canvasRef.current) return;

        // ドラッグデータからマーカーパスを取得
        const markerPath = e.dataTransfer.getData('marker');
        if (!markerPath || !MARKERS[markerPath]) return;

        // ドロップ位置を取得
        const rect = canvasRef.current.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        // 指定した位置にマーカーを追加（境界制限は handleAddMarker 内で適用される）
        handleAddMarker(markerPath, x, y);
    };

    // 画像トリミングダイアログ
    const renderCropDialog = () => {
        return (
            <Dialog
                open={cropDialogOpen}
                onClose={() => setCropDialogOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>画像をトリミング</DialogTitle>
                <DialogContent>
                    <Box sx={{ height: '60vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        {imageSrc && (
                            <Cropper
                                src={imageSrc}
                                style={{ height: '100%', width: '100%' }}
                                aspectRatio={CANVAS_ASPECT_RATIO}
                                guides={true}
                                ref={cropperRef}
                                viewMode={1}
                                minCropBoxHeight={10}
                                minCropBoxWidth={10}
                                background={false}
                                responsive={true}
                                autoCropArea={1}
                                checkOrientation={false}
                            />
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCropDialogOpen(false)} color="primary">
                        キャンセル
                    </Button>
                    <Button
                        onClick={() => {
                            const imageElement: any = cropperRef?.current;
                            const cropper: any = imageElement?.cropper;

                            if (cropper) {
                                // キャンバスからトリミングされた画像を取得
                                cropper.getCroppedCanvas().toBlob((blob: Blob) => {
                                    if (blob) {
                                        const croppedImageUrl = URL.createObjectURL(blob);
                                        handleCropComplete(croppedImageUrl);
                                    }
                                });
                            }
                        }}
                        color="primary"
                        variant="contained"
                    >
                        トリミングを確定
                    </Button>
                </DialogActions>
            </Dialog>
        );
    };

    // 初期化中の場合はローディング表示
    if (!isInitialized) {
        return (
            <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                borderRadius: 1,
                overflow: 'hidden',
                border: 1,
                borderColor: 'divider',
                justifyContent: 'center',
                alignItems: 'center'
            }}>
                <Box sx={{ p: 4, textAlign: 'center' }}>
                    <Typography variant="body2" color="textSecondary">
                        見取り図を読み込み中...
                    </Typography>
                </Box>
            </Box>
        );
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', borderRadius: 1, overflow: 'hidden', border: 1, borderColor: 'divider' }}>
            {/* トリミングダイアログ */}
            {renderCropDialog()}

            {backgroundLoadError && (
                <Alert severity="warning" sx={{ borderRadius: 0 }}>
                    背景画像を読み込めませんでした。このまま保存しても、この見取り図は編集前のまま残ります（ほかの項目は保存されます）。画面を開き直してから編集してください。
                </Alert>
            )}

            <Box sx={{ display: 'flex', flexGrow: 1, overflow: 'hidden' }}>
                {/* 左側ツールバー */}
                <Paper
                    elevation={2}
                    sx={{
                        width: 60,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        p: 0.5
                    }}
                >
                    <Tooltip title="クリア" placement="right">
                        <ToolbarButton
                            onClick={() => handleSelectTool(DrawingType.CLEAR)}
                            active={selectedTool === DrawingType.CLEAR}
                        >
                            <CleaningServices />
                        </ToolbarButton>
                    </Tooltip>
                    <Tooltip title={backgroundImage ? "背景画像を削除" : "背景画像を追加"} placement="right">
                        <ToolbarButton
                            onClick={() => handleSelectTool(DrawingType.PICTURE)}
                            active={selectedTool === DrawingType.PICTURE}
                        >
                            <Cameraswitch />
                        </ToolbarButton>
                    </Tooltip>
                    <Tooltip title="線を描く" placement="right">
                        <ToolbarButton
                            onClick={() => handleSelectTool(DrawingType.DRAW)}
                            active={selectedTool === DrawingType.DRAW}
                        >
                            <Create />
                        </ToolbarButton>
                    </Tooltip>
                    <Tooltip title="テキスト" placement="right">
                        <ToolbarButton
                            onClick={() => handleSelectTool(DrawingType.TEXT)}
                            active={selectedTool === DrawingType.TEXT}
                        >
                            <TextFields />
                        </ToolbarButton>
                    </Tooltip>

                    <Divider sx={{ width: '80%', my: 0.5 }} />

                    <Tooltip title="元に戻す" placement="right">
                        <span>
                            <ToolbarButton
                                onClick={handleUndo}
                                disabled={!(selectedTool === DrawingType.DRAW && currentPoints && currentPoints.length > 0)}
                            >
                                <Undo />
                            </ToolbarButton>
                        </span>
                    </Tooltip>
                    <Tooltip title="回転" placement="right">
                        <span>
                            <ToolbarButton
                                onClick={() => handleSelectTool(DrawingType.ROTATE)}
                                disabled={!selectedItemId}
                            >
                                <RotateLeft />
                            </ToolbarButton>
                        </span>
                    </Tooltip>
                    <Tooltip title="削除" placement="right">
                        <span>
                            <ToolbarButton
                                onClick={() => handleSelectTool(DrawingType.DELETE)}
                                disabled={!selectedItemId}
                            >
                                <Delete />
                            </ToolbarButton>
                        </span>
                    </Tooltip>
                </Paper>

                {/* 中央キャンバス */}
                <Box
                    sx={{
                        flexGrow: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        p: 1
                    }}
                >
                    <Paper
                        elevation={3}
                        sx={{
                            width: 600,
                            height: 250,
                            position: 'relative',
                            overflow: 'hidden'
                        }}
                    >
                        <canvas
                            ref={canvasRef}
                            width={600}
                            height={250}
                            onClick={handleCanvasClick}
                            onDoubleClick={handleCanvasDoubleClick}
                            onMouseDown={handleCanvasMouseDown}
                            onMouseMove={handleCanvasMouseMove}
                            onMouseUp={handleCanvasMouseUp}
                            onMouseLeave={handleCanvasMouseUp}
                            onDragOver={handleCanvasDragOver}
                            onDrop={handleCanvasDrop}
                            style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%'
                            }}
                        />
                    </Paper>
                    <Box sx={{ width: 300, mt: 1 }}>
                        <Slider
                            value={scale}
                            min={1}
                            max={5}
                            step={0.1}
                            onChange={handleScaleChange}
                            valueLabelDisplay="auto"
                            valueLabelFormat={(value) => `${value.toFixed(1)}x`}
                        />
                    </Box>
                </Box>

                {/* 右側マーカーパレット - BASE_MARKERSを使用して限定表示 */}
                <Paper
                    elevation={2}
                    sx={{
                        width: 60,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        p: 0.5
                    }}
                >
                    {Object.entries(BASE_MARKERS).map(([path, marker]) => (
                        <Tooltip key={path} title="ドラッグして配置" placement="left">
                            <MarkerItem
                                draggable
                                onDragStart={(e) => {
                                    e.dataTransfer.setData('marker', path);
                                }}
                            >
                                <Box
                                    component="img"
                                    src={marker.src}
                                    alt="マーカー"
                                    sx={{
                                        // マーカーのアスペクト比を反映
                                        width: 25,
                                        height: marker.aspectRatio ? 25 / marker.aspectRatio : 25
                                    }}
                                />
                            </MarkerItem>
                        </Tooltip>
                    ))}
                </Paper>
            </Box>

            {/* ダイアログ */}
            <Dialog
                open={confirmDialogOpen}
                onClose={() => setConfirmDialogOpen(false)}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">
                    確認
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        この見取り図をクリアしますか？
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmDialogOpen(false)}>キャンセル</Button>
                    <Button onClick={handleClearConfirm} autoFocus color="primary">
                        クリア
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={textDialogOpen}
                onClose={() => setTextDialogOpen(false)}
                maxWidth="sm" // mdよりも小さいsmを使用
                fullWidth={false} // 横幅いっぱいにしない
            >
                <DialogTitle>テキストを入力</DialogTitle>
                <DialogContent sx={{ width: 350, minHeight: 120 }}> {/* 幅と高さを少し小さく */}
                    <TextField
                        autoFocus
                        margin="dense"
                        label="テキスト"
                        fullWidth
                        multiline
                        rows={2} // 2行に減らす
                        variant="outlined"
                        value={textInput}
                        onChange={(e) => setTextInput(e.target.value)}
                        onKeyPress={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                handleAddText();
                            }
                        }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setTextDialogOpen(false)}>キャンセル</Button>
                    <Button onClick={handleAddText} color="primary">追加</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
});

export default FloorPlanEditor;